import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    Trash2, ArrowLeft, LogOut, ChevronDown, AlertTriangle, FileText, AlertCircle,
} from 'react-feather';
import config from '../../../config';
import CustomizerContext from '../../../_helper/Customizer';
import MultiDatePicker from './MultiDatePicker';
import './delete-data.css';

const MAX_DATES = 31;

const DeleteData = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { layoutURL } = useContext(CustomizerContext);

    const deletePermission = localStorage.getItem('dataDeletePermission');
    const login_id = localStorage.getItem('loginId');
    const loginName = localStorage.getItem('loginName');
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: token } };

    // Permission gate — preserved from V2 behavior. The alert+history.back is
    // crude but matches existing flow, so we keep it server-of-record honest.
    useEffect(() => {
        if (deletePermission !== 'true') {
            alert(t('You are not authorized to view this page'));
            window.history.back();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [concessionaires, setConcessionaires] = useState([]);
    const [selectedConcessionaire, setSelectedConcessionaire] = useState('');
    const [tenantCode, setTenantCode] = useState('');
    const [dates, setDates] = useState([]); // ISO yyyy-mm-dd, sorted asc
    const [reason, setReason] = useState('');
    const [tenantDetails, setTenantDetails] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    useEffect(() => {
        axios
            .post(`${config.hostname}/users/getconcessionaireaccess`, { user_id: login_id }, configheader)
            .then((response) => setConcessionaires(response.data.concessionaire_access || []))
            .catch((error) => console.error(error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        const layout = layoutURL || localStorage.getItem('sidebar_layout') || 'compact-wrapper';
        navigate(`${process.env.PUBLIC_URL}/admin/home/${layout}`);
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = `${process.env.PUBLIC_URL}/login`;
    };

    const getTenantDetails = () => {
        if (!selectedConcessionaire || !tenantCode) return;
        axios
            .post(
                `${config.hostname}/tenant/gettenantbytcode`,
                { concessionaire_id: selectedConcessionaire, tenant_code: tenantCode },
                configheader,
            )
            .then((response) => setTenantDetails(response.data))
            .catch(() => setTenantDetails(null));
    };

    const handleConcessionaireChange = (e) => {
        setSelectedConcessionaire(e.target.value);
        setTenantCode('');
        setTenantDetails(null);
    };

    const handleTCodeChange = (e) => {
        const value = e.target.value;
        setTenantCode(value);
        if (value === '') setTenantDetails(null);
    };

    const openConfirm = (e) => {
        if (e) e.preventDefault();
        if (dates.length === 0) {
            toast.error(t('Pick at least one date'));
            return;
        }
        if (dates.length > MAX_DATES) {
            toast.error(t('Maximum {{count}} dates can be deleted at a time', { count: MAX_DATES }));
            return;
        }
        setModalOpen(true);
    };

    const closeConfirm = () => setModalOpen(false);

    const handleDelete = async () => {
        try {
            await axios.post(
                `${config.hostname}/salesTransactions/delete`,
                {
                    concessionaire_id: selectedConcessionaire,
                    tcode: tenantCode,
                    dates,
                    reason,
                    created_by: loginName,
                },
                configheader,
            );
            toast.success(t('Data has been deleted'));
            setSelectedConcessionaire('');
            setTenantCode('');
            setDates([]);
            setReason('');
            setTenantDetails(null);
        } catch (error) {
            console.error(error);
            toast.error(error.message);
            throw error;
        }
    };

    return (
        <div className="v3 v3-deldata-view">
            <header className="v3-deldata-hero">
                <div className="v3-deldata-hero-icon" aria-hidden>
                    <Trash2 size={20} />
                </div>
                <div className="v3-deldata-hero-text">
                    <div className="v3-label">{t('Backend tools · Destructive')}</div>
                    <h1 className="v3-display v3-deldata-title">{t('Delete data')}</h1>
                    <p className="v3-body v3-deldata-sub">
                        {t('Permanently remove sales data for a tenant in a date window. Max 1 month per run.')}
                    </p>
                </div>
                <div className="v3-deldata-hero-actions">
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={handleBack}>
                        <ArrowLeft size={14} />
                        {t('Back to malls')}
                    </button>
                    <button type="button" className="v3-btn v3-deldata-logout" onClick={handleLogout}>
                        <LogOut size={14} />
                        {t('Logout')}
                    </button>
                </div>
            </header>

            <div className="v3-card v3-deldata-warn">
                <AlertTriangle size={16} aria-hidden />
                <span>{t('This action is permanent. Deleted sales transactions cannot be recovered.')}</span>
            </div>

            <section className="v3-card v3-deldata-form-card">
                <form onSubmit={openConfirm} className="v3-deldata-form" autoComplete="off">
                    <div className="v3-deldata-grid">
                        <div className="v3-field">
                            <label className="v3-label" htmlFor="concessionaire">{t('Mall')}</label>
                            <div className="v3-select-wrap">
                                <select
                                    id="concessionaire"
                                    className="v3-input v3-select"
                                    value={selectedConcessionaire || ''}
                                    onChange={handleConcessionaireChange}
                                    required
                                >
                                    <option value="">{t('Select a mall…')}</option>
                                    {concessionaires.map((c) => (
                                        <option key={c._id} value={c._id}>{c.concessionaire_name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={14} className="v3-select-caret" aria-hidden />
                            </div>
                        </div>

                        <div className="v3-field">
                            <label className="v3-label" htmlFor="tcode">{t('Tenant code')}</label>
                            <input
                                id="tcode"
                                type="text"
                                className="v3-input"
                                value={tenantCode || ''}
                                onChange={handleTCodeChange}
                                onBlur={getTenantDetails}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        getTenantDetails();
                                    }
                                }}
                                placeholder={t('e.g. T-0042')}
                                required
                            />
                        </div>

                        <div className="v3-field">
                            <label className="v3-label">{t('Dates to delete')}</label>
                            <MultiDatePicker
                                selected={dates}
                                onChange={setDates}
                                max={MAX_DATES}
                            />
                        </div>

                        <div className="v3-field v3-deldata-tdetails">
                            <label className="v3-label">{t('Tenant details')}</label>
                            <textarea
                                className="v3-input v3-textarea"
                                readOnly
                                required
                                value={
                                    tenantDetails
                                        ? `${t('Store name')}: ${tenantDetails.tenant_store_name || ''}\n${t('Mall code')}: ${tenantDetails.tenant_mall_code || ''}\n${t('Unit code')}: ${tenantDetails.tenant_unit_code || ''}`
                                        : ''
                                }
                                placeholder={t('Auto-fills after entering tenant code')}
                            />
                        </div>

                        <div className="v3-field v3-field-span">
                            <label className="v3-label" htmlFor="reason">
                                <FileText size={11} aria-hidden /> {t('Reason')}
                            </label>
                            <textarea
                                id="reason"
                                className="v3-input v3-textarea"
                                rows={3}
                                value={reason || ''}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder={t('Why is this data being deleted?')}
                                required
                            />
                        </div>
                    </div>

                    <div className="v3-deldata-actions">
                        <button type="submit" className="v3-btn v3-deldata-submit">
                            <Trash2 size={14} />
                            {t('Delete data')}
                        </button>
                        <button
                            type="button"
                            className="v3-btn v3-btn-ghost"
                            onClick={handleBack}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </form>
            </section>

            {modalOpen && (
                <div
                    className="v3-deldata-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="del-modal-title"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeConfirm();
                    }}
                >
                    <div className="v3-card v3-deldata-modal">
                        <div className="v3-deldata-modal-header">
                            <div className="v3-deldata-modal-icon" aria-hidden><AlertCircle size={18} /></div>
                            <div>
                                <h2 id="del-modal-title" className="v3-heading">{t('Confirm deletion')}</h2>
                                <p className="v3-body" style={{ margin: 0 }}>
                                    {t('Review the details below. This cannot be undone.')}
                                </p>
                            </div>
                        </div>

                        <dl className="v3-deldata-modal-grid">
                            <dt>{t('Tenant code')}</dt>
                            <dd><code>{tenantCode || '—'}</code></dd>
                            <dt>{t('Store name')}</dt>
                            <dd>{tenantDetails?.tenant_store_name || '—'}</dd>
                            <dt>{t('Mall code')}</dt>
                            <dd>{tenantDetails?.tenant_mall_code || '—'}</dd>
                            <dt>{t('Dates')}</dt>
                            <dd>
                                <code>{dates.length}</code> {dates.length === 1 ? t('day') : t('days')}
                                {dates.length > 0 && (
                                    <span className="v3-deldata-modal-dates">
                                        {dates.join(', ')}
                                    </span>
                                )}
                            </dd>
                        </dl>

                        <div className="v3-deldata-modal-actions">
                            <button type="button" className="v3-btn v3-btn-ghost" onClick={closeConfirm}>
                                {t('Cancel')}
                            </button>
                            <button
                                type="button"
                                className="v3-btn v3-deldata-submit"
                                disabled={confirmLoading}
                                onClick={async () => {
                                    setConfirmLoading(true);
                                    try {
                                        await handleDelete();
                                        closeConfirm();
                                    } catch (e) {
                                        // toast already shown
                                    } finally {
                                        setConfirmLoading(false);
                                    }
                                }}
                            >
                                <Trash2 size={14} />
                                {confirmLoading ? t('Deleting…') : t('Yes, delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeleteData;
